import {
  useMediaQuery,
  Box,
  Typography,
  Stack,
  Fade,
  Alert,
  Tooltip,
  IconButton,
  useTheme,
  Button,
  CircularProgress,
} from "@mui/material";
import { AppModal } from "global/components/AppModal";
import strings from "global/constants/StringConstants";
import { getCallbackKeyFromUrl, isTruthy } from "helpers/methods";
import { useAppSelector } from "hooks/use-app-select";
import { RequestFile, RequestParticipant } from "models/interfaces";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { setReduxSelectedFile, setReduxSignRequest } from "redux/appSlice";
import { useAppDispatch } from "redux/hooks";
import { RootState } from "utils/store";
import {
  FIELD_COMMIT_EVENT,
  SignerState,
  fieldsRemaining,
} from "./ParticipantScreenService";
import { globalEmitter } from "utils/emitter";
import { embedAllowsDownload, IS_EMBED } from "embed/embedMode";
import { useEmbedTheme } from "embed/useEmbedTheme";
import { saveFieldData } from "../SignRequestService";
import ParticipantDocumentViewer from "./ParticipantDocumentViewer";
import DescriptionIcon from "@mui/icons-material/Description";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DownloadDocs from "assets/icons1/DownloadDocs.svg";
import inkfreeMark from "assets/images/inkfree_collasped_logo.svg";
import ReviewerAction from "./ApproverFields";
import ChatWithPdf from "../components/ChatWithPdf";
import AIInsightsPanel from "../components/AIInsightsPanel";
import { useFeatureState } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import FeatureGateBlock from "global/components/FeatureGateBlock/FeatureGateBlock";
import Documents from "../components/Documents";
import history from "utils/history";
import { sanitizeBrandingHtml } from "utils/sanitizeHtml";
import tokens from "./SignerExperience.tokens";
import {
  fieldColor,
  isFieldVisibleTo,
} from "screens/SignRequest/SignRequestHelper";
import DocumentToolbar from "./components/DocumentToolbar";
import FieldNavigator from "./components/FieldNavigator";
import {
  EyeIcon,
  FileIcon,
  InsightsIcon,
  LockIcon,
  PencilIcon,
  SparkleIcon,
} from "./components/SignerExperienceIcons";
import { fieldNavLabel } from "./ParticipantScreenHelper";

const { palette, header, panel, canvas } = tokens;

const ParticipantDesktopScreen = (props: {
  state: SignerState;
  dispatch: Function;
  loading: boolean;
  setLoading: Function;
  selectedFile: RequestFile;
  setSelectedFile: Function;
  fileSrc: string;
  currentPageNumber: number;
  setCurrentPageNumber: Function;
  handleFileChange: Function;
  handleSubmit: Function;
  downloadFile: Function;
  currentReviewer: RequestParticipant;
  handleApproveRejectRequest: Function;
  reqToken: string;
  currentFieldOnFocus: number;
  setCurrentFieldOnFocus: Function;
  moveToFieldLocation: Function;
  signingJourneyOn: boolean;
  setSigningJourneyOn: Function;
  attachSingleCertificate: boolean;
  docLoadSuccess: boolean;
  setDocLoadSuccess: Function;
  docLoading: boolean;
  setDocLoading: Function;
  allFieldsFilled: boolean;
  allRequiredFieldsFilled: boolean;
  downloadDocs: () => void;
  downloadDocsAsZip: () => void;
  isDownloadingZip?: boolean;
  setReasonOpen: (value: boolean) => void;
  setDialogType: (value: string) => void;
  setDelegationEmail: (value: string) => void;
  allowDelegation: boolean;
}) => {
  const theme = useTheme();
  const { selectedParticipant } = useAppSelector(
    (state: RootState) => state.app,
  );
  const isMob = useMediaQuery(theme.breakpoints.down("md"));
  const urlParams = window.location.search;
  const reqToken = new URLSearchParams(urlParams).get("id");
  const [isClicked, setIsClicked] = useState(false);
  const [, setShowAlert] = useState<boolean>(!props.allFieldsFilled);
  const [openSummary, setOpenSummary] = useState<boolean>(false);
  const [summary] = useState<any>();
  // Counter for the event-driven batch save below. Increments on every
  // field commit (text blur with change, checkbox toggle, signature complete,
  // etc.) and resets after each successful saveFieldData call.
  const commitCountRef = useRef<number>(0);
  // Mirrors the latest signRequest so the emitter listener can read it
  // without re-subscribing on every signRequest change.
  const signRequestRef = useRef(props.state.signRequest);
  const dispatch = useAppDispatch();
  const [activeRightPanel, setActiveRightPanel] = useState<
    "chat" | "insights" | "documents" | null
  >("documents");
  const currentSigner = props.state.signRequest.signers.find(
    (s) => s.email === selectedParticipant.email,
  );

  const files = props.state.signRequest.files ?? [];

  // Signing-page branding resolved by the backend (workspace branding when enabled, else the
  // account's). Only its colours apply here — the header always shows the Inkfree mark.
  const branding: any = (props.state.signRequest as any)?.branding;
  // Embedded: the parent page's theme override (SDK) wins over the tenant branding.
  const embedTheme = useEmbedTheme();
  const brandPrimary =
    embedTheme.primaryColor || branding?.primaryColor || palette.ctaBg;
  const brandBtnText =
    embedTheme.buttonTextColor || branding?.buttonTextColor || palette.white;
  // The call-to-action colour for every other accent (panel rail, "Start signing"): the signer theme's
  // orange group. Outside an embed it is the same brand red as `palette.ctaBg`; in an embed it follows
  // the partner's colour through the theme the screen is wrapped in.
  const ctaMain = theme.palette.signer.orange.main;
  const ctaHover = theme.palette.signer.orange.dark;

  // Reviewer-only colour key: maps each recipient to the colour their fields
  // use on the document (`fieldColor[field.signerIndex]`), so a reviewer can
  // tell at a glance which field belongs to whom. Only recipients that actually
  // have a placed field are listed.
  const isReviewer = isTruthy(props.currentReviewer.email);
  const reviewerLegend = isReviewer
    ? (props.state.signRequest.signers ?? [])
        .map((signer: any, index: number) => {
          const colorIndex = signer.id ?? index;
          return {
            colorIndex,
            name: signer.name || signer.email || `Recipient ${index + 1}`,
            color:
              fieldColor[colorIndex] ?? fieldColor[index % fieldColor.length],
          };
        })
        .filter((entry) =>
          files.some((file: any) =>
            (file.fields ?? []).some(
              (f: any) => f.signerIndex === entry.colorIndex && !f.hidden,
            ),
          ),
        )
    : [];

  const activeFileIndex = Math.max(
    files.findIndex(
      (file: RequestFile) =>
        file.unsignedPdf === props.selectedFile?.unsignedPdf ||
        (file as any).unsignedDoc === (props.selectedFile as any)?.unsignedDoc,
    ),
    0,
  );
  /** Matches how the viewer pairs a field to its owner (`field.signerIndex`). */
  const signerFieldIndex =
    currentSigner?.id ??
    props.state.signRequest.signers.findIndex(
      (s) => s.email === selectedParticipant.email,
    );

  // Field navigator state. Fields are addressed by their global 1..N id, which
  // is what moveToFieldLocation walks, so position === currentFieldOnFocus.
  const totalFields = fieldsRemaining(files).totalFields;
  const focusedField = files
    .flatMap((file: RequestFile) => file.fields ?? [])
    .find((field: any) => field.id === props.currentFieldOnFocus);
  const activeDocIsReviewOnly =
    files.length > 0 &&
    !isTruthy(props.currentReviewer.email) &&
    !(files[activeFileIndex]?.fields ?? []).some(
      (field: any) => isFieldVisibleTo(field, signerFieldIndex) && !field.hidden,
    );
  const showFieldNavigator =
    totalFields > 0 &&
    !isTruthy(props.fileSrc) &&
    !isTruthy(props.currentReviewer.email) &&
    props.state.signRequest.status === strings.pending &&
    currentSigner?.status === strings.pending;

  const aiAssistantState = useFeatureState(FeatureKeys.AI_ASSISTANT);
  const isAIAvailable =
    aiAssistantState.status === strings.FEATURE_STATUS_AVAILABLE;
  const aiDisabledTooltip =
    aiAssistantState.status === strings.FEATURE_STATUS_ADMIN_DISABLED
      ? ((aiAssistantState as any).disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
      : aiAssistantState.status === strings.FEATURE_STATUS_NOT_IN_PLAN
        ? strings.FEATURE_GATE_UPSELL_TOOLTIP
        : "";

  // Right panel icons configuration
  const rightPanelIcons = [
    {
      id: "documents" as const,
      icon: FileIcon,
      label: "Documents",
      title: "Documents",
    },
    {
      id: "chat" as const,
      icon: SparkleIcon,
      label: "AI Assistant",
      title: "AI Assistant",
    },
    {
      id: "insights" as const,
      icon: InsightsIcon,
      label: "Insights",
      title: "AI Insights",
    },
  ];

  const handleRightPanelChange = useCallback(
    (panelId: "chat" | "insights" | "documents") => {
      setActiveRightPanel(panelId);
    },
    [],
  );

  // Memoized right sidebar content
  const RightSidebarContent = useMemo(
    () => (
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "row",
          backgroundColor: panel.background,
        }}
      >
        {/* Content Panel */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Content based on active panel */}
          <Box
            sx={{
              flex: 1,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {activeRightPanel === "documents" && (
              <Documents
                docLoading={props.docLoading}
                onFileChange={(file) => {
                  if (!props.docLoading) {
                    props.setDocLoading(true);
                    dispatch(setReduxSelectedFile(file));
                    props.handleFileChange(
                      file.unsignedPdf || file.unsignedDoc,
                    );
                  }
                }}
              />
            )}
            {activeRightPanel === "chat" &&
              (isAIAvailable ? (
                <ChatWithPdf
                  signRequest={props.state.signRequest}
                  isPublic={true}
                  requesToken={props.reqToken}
                />
              ) : (
                <FeatureGateBlock
                  variant="inline"
                  status={
                    aiAssistantState.status ===
                    strings.FEATURE_STATUS_ADMIN_DISABLED
                      ? strings.FEATURE_STATUS_ADMIN_DISABLED
                      : strings.FEATURE_STATUS_NOT_IN_PLAN
                  }
                  disabledMessage={(aiAssistantState as any).disabledMessage}
                />
              ))}
            {activeRightPanel === "insights" && (
              <AIInsightsPanel
                signRequest={props.state.signRequest}
                isPublic={true}
                requesToken={props.reqToken}
              />
            )}
          </Box>
        </Box>

        <Box
          sx={{
            width: panel.railWidth,
            flexShrink: 0,
            borderLeft: panel.borderLeft,
            backgroundColor: panel.background,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            pt: panel.railPaddingTop,
            gap: panel.railGap,
          }}
        >
          {rightPanelIcons.map((panelIcon) => {
            const IconComponent = panelIcon.icon;
            const isActive = activeRightPanel === panelIcon.id;
            const isAIFeature =
              panelIcon.id === "chat" || panelIcon.id === "insights";
            const isLocked = isAIFeature && !isAIAvailable;
            const tooltipTitle = isLocked
              ? `${panelIcon.label} — ${aiDisabledTooltip}`
              : panelIcon.title;
            return (
              <Box
                key={panelIcon.id}
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Tooltip title={tooltipTitle} placement="left" arrow>
                  <IconButton
                    onClick={() => handleRightPanelChange(panelIcon.id)}
                    aria-pressed={isActive}
                    sx={{
                      width: panel.railButtonSize,
                      height: panel.railButtonSize,
                      borderRadius: panel.railButtonRadius,
                      background: isActive ? ctaMain : palette.railRest,
                      color: isActive ? palette.white : palette.meta,
                      "&:hover": {
                        background: isActive
                          ? ctaHover
                          : palette.accentTint,
                      },
                      transition: "background 0.2s ease, color 0.2s ease",
                      opacity: isLocked && !isActive ? 0.65 : 1,
                    }}
                  >
                    <IconComponent size={panel.railIconSize} />
                  </IconButton>
                </Tooltip>
                {isLocked && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 2,
                      right: 2,
                      backgroundColor: palette.white,
                      borderRadius: "50%",
                      width: 14,
                      height: 14,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: palette.meta,
                      boxShadow: `0 0 0 1px ${palette.border}`,
                      pointerEvents: "none",
                    }}
                  >
                    <LockIcon size={10} />
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    ),
    [
      activeRightPanel,
      handleRightPanelChange,
      theme,
      props.docLoading,
      props.state.signRequest,
      props.reqToken,
      isAIAvailable,
      aiAssistantState,
      aiDisabledTooltip,
    ],
  );

  useEffect(() => {
    // Set signRequest in Redux for Documents component
    dispatch(setReduxSignRequest(props.state.signRequest));
  }, [props.state.signRequest, dispatch]);

  // Keep signRequestRef in sync so the commit listener always sees the
  // latest snapshot when it fires saveFieldData.
  useEffect(() => {
    signRequestRef.current = props.state.signRequest;
  }, [props.state.signRequest]);

  // Event-driven batch save: every COMMITS_PER_SAVE field commits, fire
  // saveFieldData once and reset the counter. No timers — purely
  // event-driven so we don't introduce dirty-read/write races between a
  // scheduled save and the next fetch.
  useEffect(() => {
    const COMMITS_PER_SAVE = 5;
    const handleCommit = () => {
      const signRequest = signRequestRef.current;
      const isPending =
        signRequest?.status === strings.pending &&
        signRequest?.files?.length > 0;
      if (!reqToken || !isPending) return;

      commitCountRef.current += 1;
      if (commitCountRef.current >= COMMITS_PER_SAVE) {
        commitCountRef.current = 0;
        saveFieldData(signRequest, reqToken).catch((err) =>
          console.error("Error saving field data:", err),
        );
      }
    };

    globalEmitter.on(FIELD_COMMIT_EVENT, handleCommit);
    return () => {
      globalEmitter.off(FIELD_COMMIT_EVENT, handleCommit);
    };
  }, [reqToken]);

  useEffect(() => {
    // Set initial selected file in Redux for Documents component
    if (props.selectedFile) {
      const normalized: any = {
        ...props.selectedFile,
        unsignedDoc:
          (props.selectedFile as any).unsignedDoc ||
          (props.selectedFile as any).unsignedPdf ||
          "",
        unsignedDocUrl:
          (props.selectedFile as any).unsignedDocUrl ||
          (props.selectedFile as any).unsignedPdfUrl ||
          "",
      };
      dispatch(setReduxSelectedFile(normalized));
    }
  }, [props.selectedFile, dispatch]);

  useEffect(() => {
    if (isClicked && !props.allRequiredFieldsFilled) {
      const timer = setTimeout(() => {
        setIsClicked(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isClicked, props.allRequiredFieldsFilled]);

  const handleFinishAction = () => {
    if (!props.allRequiredFieldsFilled) {
      setIsClicked(true);
      props.setSigningJourneyOn(true);
      props.moveToFieldLocation(1, true);
    } else {
      props.handleSubmit();
    }
  };

  const renderSummaryContent = (data: any, level = 0) => {
    if (!data) return null;

    return Object.entries(data).map(([key, value]) => {
      if (typeof value === "object") {
        return (
          <Box
            key={key}
            sx={{
              mb: 3,
              pl: level * 1.5,
              borderLeft: level > 0 ? "2px solid rgba(0,0,0,0.1)" : "none",
              paddingLeft: level > 0 ? 2 : 0,
              textAlign: "left",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 1.5,
                fontSize: "1.1rem",
                color: "#333",
              }}
            >
              {key}
            </Typography>
            <Box sx={{ pl: 2.5, m: 0 }}>
              {renderSummaryContent(value, level + 1)}
            </Box>
          </Box>
        );
      } else {
        return (
          <Box
            key={key}
            sx={{
              mb: 1,
              pl: 1.5,
              position: "relative",
              "&::before": {
                content: '"•"',
                position: "absolute",
                left: 0,
                color: "#555",
              },
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontSize: "0.95rem",
                lineHeight: 1.5,
                color: "#444",
              }}
            >
              {typeof value === "string" ? value : ""}
            </Typography>
          </Box>
        );
      }
    });
  };

  const headerContent = () => {
    const isSigningNow =
      props.state.signRequest.status === strings.pending &&
      currentSigner?.status === strings.pending &&
      !isTruthy(props.fileSrc);
    const fields = fieldsRemaining(props.state.signRequest.files);
    const remaining = fields.requiredValue - fields.requiredWithValue;
    const chipDone = remaining <= 0;

    return (
      <Box
        component="header"
        sx={{
          height: header.height,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: header.gap,
          px: header.paddingX,
          boxSizing: "border-box",
          backgroundColor: header.background,
          borderBottom: header.borderBottom,
          zIndex: 30,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: header.identityGap,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box
            component="img"
            src={inkfreeMark}
            alt="Inkfree"
            sx={{ width: header.markWidth, height: "auto", flexShrink: 0 }}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: header.greetingGap,
              minWidth: 0,
            }}
          >
            <Typography
              component="h1"
              sx={{
                fontSize: header.greetingFontSize,
                fontWeight: header.greetingFontWeight,
                lineHeight: header.greetingLineHeight,
                color: header.greetingColor,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              Hello,{" "}
              {isTruthy(props.currentReviewer.email)
                ? props.currentReviewer?.name
                : props.state.signRequest.status === strings.pending ||
                    props.state.signRequest.status === strings.signed
                  ? selectedParticipant.name
                  : ""}
            </Typography>
            <Typography
              sx={{
                fontSize: header.subtitleFontSize,
                lineHeight: header.greetingLineHeight,
                color: header.subtitleColor,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {!props.fileSrc
                ? isTruthy(props.currentReviewer.email)
                  ? `${props.state.signRequest?.requestedBy?.name || "The sender"} asked you to review this document`
                  : `${props.state.signRequest?.requestedBy?.name || "The sender"} asked you to sign this document`
                : isTruthy(props.currentReviewer.email)
                  ? "Thanks for taking the time to review the document."
                  : "Thank you. The document has been successfully signed and recorded."}
            </Typography>
          </Box>
        </Box>

        {/* Fields-remaining chip + Start signing */}
        {isSigningNow && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: header.groupGap,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: header.chipGap,
                flexShrink: 0,
                height: header.chipHeight,
                px: header.chipPaddingX,
                borderRadius: header.chipRadius,
                backgroundColor: chipDone
                  ? palette.successTint
                  : palette.accentTint,
                border: `1px solid ${
                  chipDone ? palette.successBorder : palette.accentBorder
                }`,
              }}
            >
              <Box
                sx={{
                  width: header.chipDotSize,
                  height: header.chipDotSize,
                  borderRadius: "50%",
                  backgroundColor: chipDone ? palette.success : palette.accent,
                }}
              />
              <Typography
                sx={{
                  fontSize: header.chipFontSize,
                  fontWeight: header.chipFontWeight,
                  whiteSpace: "nowrap",
                  color: chipDone ? palette.success : palette.accentDark,
                }}
              >
                {chipDone
                  ? "All fields complete"
                  : `${remaining} required field${remaining === 1 ? "" : "s"} left`}
              </Typography>
            </Box>

            <Button
              variant="contained"
              disableElevation
              startIcon={<PencilIcon size={header.startIconSize} />}
              onClick={() => {
                props.setSigningJourneyOn(true);
                props.moveToFieldLocation(1, true);
              }}
              disabled={props.docLoading}
              sx={{
                flexShrink: 0,
                height: header.startHeight,
                px: header.startPaddingX,
                minWidth: "auto",
                borderRadius: header.startRadius,
                textTransform: "none",
                fontSize: header.startFontSize,
                fontWeight: header.startFontWeight,
                boxShadow: "none",
                background: chipDone ? palette.successTint : ctaMain,
                color: chipDone ? palette.success : palette.white,
                "& .MuiButton-startIcon": {
                  mr: header.startGap,
                  ml: 0,
                },
                "&:hover": {
                  boxShadow: "none",
                  background: chipDone
                    ? palette.successTint
                    : ctaHover,
                },
                "&.Mui-disabled": {
                  background: ctaMain,
                  color: palette.white,
                  opacity: 0.5,
                },
              }}
            >
              {chipDone ? "Signed" : "Start signing"}
            </Button>
          </Box>
        )}

        {isSigningNow && (
          <Box
            sx={{
              width: header.dividerWidth,
              height: header.dividerHeight,
              flexShrink: 0,
              backgroundColor: header.dividerColor,
            }}
          />
        )}

        {/* Decline / Delegate / Finish */}
        <Box
          sx={{ display: "flex", justifyContent: "flex-end", flexShrink: 0 }}
        >
          {props.state.signRequest.status === strings.pending &&
          currentSigner?.status === strings.pending ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: header.actionsGap,
                flexShrink: 0,
              }}
            >
              <Button
                variant="outlined"
                title="Decline this request"
                onClick={() => {
                  const t = new URLSearchParams(window.location.search).get(
                    "id",
                  );
                  if (t) {
                    history.push(
                      `/decline/request?id=${t}&key=${encodeURIComponent(getCallbackKeyFromUrl())}&signer=true`,
                    );
                  } else {
                    props.setDialogType(strings.decline);
                    props.setReasonOpen(true);
                  }
                }}
                disabled={props.loading || props.docLoading}
                sx={{
                  height: header.actionHeight,
                  px: header.actionPaddingX,
                  minWidth: "auto",
                  textTransform: "none",
                  fontWeight: header.actionFontWeight,
                  fontSize: header.actionFontSize,
                  borderRadius: header.actionRadius,
                  color: palette.meta,
                  background: palette.white,
                  border: `${header.actionBorderWidth} solid ${palette.border}`,
                  "&:hover": {
                    border: `${header.actionBorderWidth} solid #EBC5C2`,
                    color: "#B4372F",
                    background: palette.white,
                  },
                }}
              >
                Decline
              </Button>
              {props.allowDelegation && (
                <Button
                  variant="outlined"
                  title="Delegate to someone else"
                  onClick={() => {
                    props.setDialogType(strings.delegate);
                    props.setReasonOpen(true);
                  }}
                  disabled={props.loading || props.docLoading}
                  sx={{
                    height: header.actionHeight,
                    px: header.actionPaddingX,
                    minWidth: "auto",
                    textTransform: "none",
                    fontWeight: header.actionFontWeight,
                    fontSize: header.actionFontSize,
                    borderRadius: header.actionRadius,
                    color: palette.meta,
                    background: palette.white,
                    border: `${header.actionBorderWidth} solid ${palette.border}`,
                    "&:hover": {
                      border: `${header.actionBorderWidth} solid #D8CBEE`,
                      color: palette.purple,
                      background: palette.white,
                    },
                  }}
                >
                  Delegate
                </Button>
              )}
              <Button
                variant="contained"
                disableElevation
                onClick={handleFinishAction}
                disabled={props.loading || props.docLoading}
                title={
                  props.allRequiredFieldsFilled
                    ? "Complete and send back"
                    : "Sign the required field first"
                }
                sx={{
                  height: header.actionHeight,
                  px: header.finishPaddingX,
                  minWidth: "auto",
                  textTransform: "none",
                  fontWeight: header.actionFontWeight,
                  fontSize: header.finishFontSize,
                  borderRadius: header.actionRadius,
                  boxShadow: "none",
                  color: brandBtnText,
                  background: props.allRequiredFieldsFilled
                    ? brandPrimary
                    : palette.inactive,
                  cursor: props.allRequiredFieldsFilled
                    ? "pointer"
                    : "not-allowed",
                  "&:hover": {
                    boxShadow: "none",
                    background: props.allRequiredFieldsFilled
                      ? brandPrimary
                      : palette.inactive,
                  },
                  "&.Mui-disabled": {
                    color: brandBtnText,
                    background: props.allRequiredFieldsFilled
                      ? brandPrimary
                      : palette.inactive,
                    opacity: 0.6,
                  },
                }}
              >
                Finish
              </Button>
            </Box>
          ) : props.state.signRequest.status === strings.signed &&
            embedAllowsDownload(props.state.signRequest) ? (
              <Stack direction={"row"} spacing={2}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    <img src={DownloadDocs} alt="" aria-hidden="true" />
                  }
                  aria-label="Download Documents as a zip"
                  onClick={() => {
                    props.downloadDocs();
                  }}
                >
                  {strings.downloadDoc}
                </Button>
                <Button
                  variant={"outlined"}
                  onClick={props.downloadDocsAsZip}
                  size="small"
                  disabled={props.isDownloadingZip}
                  startIcon={
                    props.isDownloadingZip ? (
                      <CircularProgress
                        size={16}
                        thickness={5}
                        sx={{ color: theme.palette.primary.main }}
                      />
                    ) : undefined
                  }
                  sx={{
                    width: "fit-content",
                    "&.Mui-disabled": {
                      color: theme.palette.primary.main,
                      borderColor: `${theme.palette.primary.main}66`,
                    },
                  }}
                >
                  {props.isDownloadingZip
                    ? "Downloading zip..."
                    : strings.downloadDocsAsZip}
                </Button>
              </Stack>
            ) : (
              props.state.signRequest.status === strings.inReview &&
              isTruthy(props.currentReviewer.email) && (
                <>
                  <ReviewerAction
                    requestStatus={props.state.signRequest.status}
                    currentReviewer={props.currentReviewer!}
                    reviewers={props.state.signRequest.approval.reviewers}
                    handleApproveRejectRequest={
                      props.handleApproveRejectRequest
                    }
                  />
                </>
              )
            )}
          </Box>
      </Box>
    );
  };

  return (
    <Box
      onClick={() => setShowAlert(false)}
      onKeyPress={(e) => {
        const target = e.target as HTMLElement;
        const isInputField =
          target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable;
        if ((e.key === "Enter" || e.key === " ") && !isInputField) {
          setShowAlert(false);
        }
      }}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
        backgroundColor: palette.canvas,
      }}
    >
      {headerContent()}
      {isReviewer && reviewerLegend.length > 0 && (
        <Box
          aria-label="Field colour key by recipient"
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.75,
            px: { xs: 2, md: 3 },
            py: 1,
            flexShrink: 0,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.common.white,
          }}
        >
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 700,
              color: theme.palette.text.secondary,
              whiteSpace: "nowrap",
            }}
          >
            Fields by recipient:
          </Typography>
          {reviewerLegend.map((entry) => (
            <Box
              key={entry.colorIndex}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  width: 13,
                  height: 13,
                  borderRadius: "4px",
                  flexShrink: 0,
                  backgroundColor: entry.color,
                  border: "1px solid rgba(0, 0, 0, 0.12)",
                }}
              />
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 180,
                }}
              >
                {entry.name}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      {isClicked && !props.allRequiredFieldsFilled && (
        <Fade in={isClicked} timeout={1000}>
          <Alert
            severity="warning"
            sx={{
              width: "fit-content",
              position: "fixed",
              top: "20px",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 9999,
            }}
          >
            Please fill all required fields!
          </Alert>
        </Fade>
      )}
      <Box sx={{ flex: 1, display: "flex", minHeight: 0 }}>
        {/* Document canvas */}
        <Box
          component="main"
          id="main-content"
          tabIndex={-1}
          aria-label="Document to sign"
          sx={{
            flex: 1,
            minWidth: 0,
            position: "relative",
            outline: "none",
            display: "flex",
            flexDirection: "column",
            backgroundColor: palette.canvas,
          }}
        >
          <ParticipantDocumentViewer
            docLoading={props.docLoading}
            setDocLoading={props.setDocLoading}
            docLoadSuccess={props.docLoadSuccess}
            setDocLoadSuccess={props.setDocLoadSuccess}
            signerSelectedFile={
              isTruthy(props.fileSrc) ? props.fileSrc : props.selectedFile
            }
            setSignerSelectedFile={props.setSelectedFile}
            requestToken={props.reqToken!}
            state={props.state}
            setLoading={props.setLoading}
            dispatch={props.dispatch}
            selectedFile={props.selectedFile}
            setSelectedFile={props.setSelectedFile}
            currentReviewer={props.currentReviewer}
            currentPageNumber={props.currentPageNumber}
            setCurrentFieldOnFocus={props.setCurrentFieldOnFocus}
            currentFieldOnFocus={props.currentFieldOnFocus}
            moveToFieldLocation={props.moveToFieldLocation}
            signingJourneyOn={props.signingJourneyOn}
            loading={props.loading}
            fileSrc={props.fileSrc}
            setCurrentPageNumber={props.setCurrentPageNumber}
            handleFileChange={props.handleFileChange}
            handleSubmit={props.handleSubmit}
            downloadFile={props.downloadFile}
            handleApproveRejectRequest={props.handleApproveRejectRequest}
            reqToken={props.reqToken!}
            setSigningJourneyOn={props.setSigningJourneyOn}
            attachSingleCertificate={props.attachSingleCertificate}
            allFieldsFilled={props.allFieldsFilled}
            canvasChrome
            renderToolbar={({ numPages, currentPage, goToPage }) => (
              <>
                <DocumentToolbar
                  files={files}
                  activeIndex={activeFileIndex}
                  signerIndex={signerFieldIndex}
                  disabled={props.docLoading}
                  onPickDocument={(file) => {
                    props.setDocLoading(true);
                    dispatch(setReduxSelectedFile(file));
                    props.handleFileChange(
                      (file as any).unsignedPdf || (file as any).unsignedDoc,
                    );
                  }}
                  numPages={numPages}
                  currentPage={currentPage}
                  onGoToPage={goToPage}
                />
                {activeDocIsReviewOnly && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: canvas.reviewPillGap,
                      width: "fit-content",
                      height: canvas.reviewPillHeight,
                      flexShrink: 0,
                      px: canvas.reviewPillPaddingX,
                      mb: canvas.reviewPillMarginBottom,
                      borderRadius: canvas.reviewPillRadius,
                      backgroundColor: canvas.reviewPillBg,
                      border: canvas.reviewPillBorder,
                      color: palette.purple,
                    }}
                  >
                    <EyeIcon size={canvas.reviewPillIconSize} />
                    <Typography
                      sx={{
                        fontSize: canvas.reviewPillFontSize,
                        fontWeight: canvas.reviewPillFontWeight,
                        color: "inherit",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Review only · no fields for you in this document
                    </Typography>
                  </Box>
                )}
              </>
            )}
          />

          {showFieldNavigator && (
            <FieldNavigator
              label={fieldNavLabel(focusedField)}
              position={Math.min(props.currentFieldOnFocus, totalFields)}
              total={totalFields}
              filled={isTruthy(focusedField?.value)}
              onPrevious={() =>
                props.moveToFieldLocation(
                  props.currentFieldOnFocus <= 1
                    ? totalFields
                    : props.currentFieldOnFocus - 1,
                )
              }
              onNext={() =>
                props.moveToFieldLocation(
                  props.currentFieldOnFocus >= totalFields
                    ? 1
                    : props.currentFieldOnFocus + 1,
                )
              }
            />
          )}
        </Box>

        {/* Documents / AI panel */}
        {!isMob && (
          <Box
            component="aside"
            aria-label="Document tools"
            sx={{
              width: panel.width,
              flexShrink: 0,
              display: "flex",
              minHeight: 0,
              backgroundColor: panel.background,
              borderLeft: panel.borderLeft,
            }}
          >
            {RightSidebarContent}
          </Box>
        )}
      </Box>
      <AppModal
        open={openSummary}
        onClose={() => setOpenSummary(false)}
        title="Document Summary"
        // Keeps the document glyph the summary has always carried.
        icon={<DescriptionIcon sx={{ fontSize: 19 }} />}
        tone="neutral"
        // A long read — the design system's L width.
        size="lg"
        // The AI disclaimer sits in the footer bar in place of an action.
        footer={
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              p: 1.5,
              borderRadius: "8px",
              background: "rgba(25, 118, 210, 0.04)",
              border: "1px solid rgba(25, 118, 210, 0.1)",
            }}
          >
            <InfoOutlinedIcon
              sx={{ color: "#1976d2", mr: 1.5, mt: "2px", fontSize: "1rem" }}
            />
            <Typography
              fontSize="0.78rem"
              sx={{ fontStyle: "italic", color: "#555", lineHeight: 1.5 }}
            >
              This AI-generated summary is provided for convenience, but it may
              not reflect the full content or intent of the original document.
              For complete and precise information, please read the original
              document.
            </Typography>
          </Box>
        }
      >
        {summary ? (
          renderSummaryContent(summary.summary)
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 6,
              opacity: 0.7,
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: "3rem", color: "#646464", mb: 2 }} />
            <Typography
              variant="body1"
              sx={{ textAlign: "center", color: "#646464" }}
            >
              No summary data available
            </Typography>
          </Box>
        )}
      </AppModal>
      {!IS_EMBED && branding?.footerHtml && (
        <Box
          sx={{
            borderTop: "1px solid #eee",
            px: 3,
            py: 1.5,
            textAlign: "center",
            fontSize: "0.75rem",
            color: "#666",
          }}
          // Admin/workspace-configured branding footer (same content as branded emails).
          // Sanitized: the signing page is shown to external signers, so admin HTML must
          // not be able to inject scripts/handlers (stored XSS) into their session.
          dangerouslySetInnerHTML={{
            __html: sanitizeBrandingHtml(branding.footerHtml),
          }}
        />
      )}
    </Box>
  );
};

export default ParticipantDesktopScreen;
